import { useState } from 'react'
import styled from 'styled-components'
import { Skeleton, Tooltip } from '@mui/material'
import { HelpOutline, Delete } from '@mui/icons-material'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useIntakeList, useQuery } from '../../hooks'
import { timestampToDDMMHHMM } from '../../utils/dateConversion'
import { Sleep } from './Sleep'
import { Caffeine } from './Caffeine'
import { useTranslation } from "react-i18next"
import { AutoCompleteWrapper } from "../../components"
import { RequestService } from '../../services/RequestService'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 44px;
`
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const CategoryGroup = styled.div`
  display: flex;
  flex: 1;
  gap: 48px;
`

const Category = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 24px;
  gap: 16px;
`

const CategoryItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 32px;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  &:hover {
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.1);
    z-index: 1;
  }
`

const DeleteButton = styled(Delete)`
  position: absolute;
  top: 8px;
  right: 8px;
  color: #888;
  cursor: pointer;
  padding: 2px;
  &:hover {
    color: red;
  }
`
const SystemAltIcon = styled(SystemUpdateAltIcon)`
  position: absolute;
  top: 8px;
  right: 8px;
  color: #888;
  cursor: pointer;
  padding: 2px;
`

const PAGES = {
  LIST: 'list',
  CAFFEINE: 'caffeine',
  SLEEP: 'sleep',
}

const IntakeList = () => {
  const [page, setPage] = useState(PAGES.LIST)
  const [sleepId, setSleepId] = useState(null)
  const [caffeineId, setCaffeineId] = useState(null)
  const [sleepTime, setSleepTime] = useState(null)
  const [awakeTime, setAwakeTime] = useState(null)
  const [caffeineProducts, setCaffeineProducts] = useState([])
  const [selectedCaffeineOptions, setSelectedCaffeineOptions] = useState([])
  const [caffeineTime, setCaffeineTime] = useState(null)
  const query = useQuery()
  const [userId, setUserId] = useState(query.get('user_id'))
  let { data, loading, deleteIntake } = useIntakeList({ page, userId })
  const { t } = useTranslation('common')
  const [selectedUser, setSelectedUser] = useState(null)
  const [inputValue, setInputValue] = useState('')

  const resetStates = () => {
    setSleepTime(null)
    setAwakeTime(null)
    setCaffeineProducts([])
    setSelectedCaffeineOptions([])
    setCaffeineTime(null)
    setSleepId(null)
    setCaffeineId(null)
  }

  const onSelectUser = value => {
    setSelectedUser(value)
    setInputValue(value?.name)
    setUserId(value?.id)
  }

  const handleOnPrevious = () => {
    resetStates()
    setPage(PAGES.LIST)
  }

  const handleOnFinish = () => {
    resetStates()
    setPage(PAGES.LIST)
  }

  const handleSleepIntake = ({ sleepStart, sleepEnd, id }) => {
    setSleepTime(sleepStart)
    setAwakeTime(sleepEnd)
    setSleepId(id)
    setPage(PAGES.SLEEP)
  }

  const handleCaffeineIntake = ({ id, caffeineTaken }) => {
    setCaffeineId(id)
    setCaffeineTime(caffeineTaken)
    setPage(PAGES.CAFFEINE)
  }

  const handleDeleteIntake = (type, id) => {
    if (window.confirm(t('intake_list.delete_confirm'))) {
      deleteIntake(type, id)
    }
  }

  if (page === PAGES.SLEEP)
    return (
      <Sleep
        id={sleepId}
        sleepTime={sleepTime}
        awakeTime={awakeTime}
        onSleepChange={setSleepTime}
        onAwakeChange={setAwakeTime}
        onPrevious={handleOnPrevious}
        onFinish={handleOnFinish}
      />
    )

  if (page === PAGES.CAFFEINE)
    return (
      <Caffeine
        id={caffeineId}
        caffeineProducts={caffeineProducts}
        setCaffeineProducts={setCaffeineProducts}
        caffeineTime={caffeineTime}
        onChangeCaffeineTime={setCaffeineTime}
        selectedCaffeineOptions={selectedCaffeineOptions}
        onSelectCaffeineOptions={setSelectedCaffeineOptions}
        onPrevious={handleOnPrevious}
        onFinish={handleOnFinish}
      />
    )

  return (
    <Container>
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            {t('intake_list.header')}
          </span>
          <Tooltip
            title={t('intake_list.tooltip')}
            placement='right'
            arrow
          >
            <HelpOutline fontSize='18' />
          </Tooltip>
        </div>
      </Header>
      <div style={{ display: 'flex', flex: 1.5 }}>
        <AutoCompleteWrapper
          onLoad={() => RequestService.getUsers()}
          value={selectedUser}
          onChangeValue={onSelectUser}
          inputValue={inputValue}
          onInputValueChange={setInputValue}
        />
      </div>
      <CategoryGroup>
        <Category>
          <span style={{ fontSize: 18, fontWeight: 600 }}>Sono</span>

          {!loading ? (
            <>
              {data?.sleepIntakes.map(intake => {
                const sleepStart = new Date(intake?.sleepStart)
                const sleepEnd = new Date(intake?.sleepEnd)

                return (
                  <CategoryItem
                    onClick={() =>
                      handleSleepIntake({
                        sleepStart,
                        sleepEnd,
                        id: intake?.id,
                      })
                    }
                  >
                  { intake.createdBy === 'system' ?
                    (

                      <SystemAltIcon />
                    )
                    :
                    (
                      <DeleteButton
                        onClick={e => {
                          e.stopPropagation()
                          handleDeleteIntake('sleep', intake?.id)
                        }}
                      />
                    )
                  }
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{t('intake_list.slept')}</span>
                      <span>
                        {timestampToDDMMHHMM({
                          timestamp: sleepStart.getTime(),
                        })}
                      </span>
                    </div>
                    <hr />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{t('intake_list.awoke')}</span>
                      <span>
                        {timestampToDDMMHHMM({
                          timestamp: sleepEnd.getTime(),
                        })}
                      </span>
                    </div>
                  </CategoryItem>
                )
              })}
            </>
          ) : (
            <Skeleton
              variant='rectangular'
              height={100}
            />
          )}
        </Category>

        <Category>
          <span style={{ fontSize: 18, fontWeight: 600 }}>Cafeína</span>

          {!loading ? (
            <>
              {data?.caffeineIntakes.map(intake => {
                const caffeineTaken = new Date(intake?.caffeineTaken)

                return (
                  <CategoryItem
                    onClick={() =>
                      handleCaffeineIntake({ id: intake?.id, caffeineTaken })
                    }
                  >
                    { intake.createdBy === 'system' ?
                      (

                        <SystemAltIcon />
                      )
                      :
                      (
                        <DeleteButton
                          onClick={e => {
                            e.stopPropagation()
                            handleDeleteIntake('caffeine', intake?.id)
                          }}
                        />
                      )
                    }
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{t('intake_list.datetime')}</span>
                      <span>
                        {timestampToDDMMHHMM({
                          timestamp: caffeineTaken.getTime(),
                        })}
                      </span>
                    </div>
                    <hr />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{t('intake_list.ingested')}</span>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {intake.caffeineProducts?.map(product => (
                          <span
                            style={{
                              maxWidth: 180,
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              textAlign: 'right',
                            }}
                          >
                            {product}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CategoryItem>
                )
              })}
            </>
          ) : (
            <Skeleton
              variant='rectangular'
              height={100}
            />
          )}
        </Category>
      </CategoryGroup>
    </Container>
  )
}

export default IntakeList
